// Produtos.tsx
//
// A lista dos produtos da rede, com a categoria e o preço já resolvidos pela
// junção (o preço vive na categoria, não no produto). Somente leitura: não há
// linha clicável, não há ação de linha, não há salvar.
import React, { useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/cardapioSection/index";
import { Cardapio, formatarReais } from "./regrasDeSaneamento";

interface Props {
  cardapio: Cardapio;
}

/**
 * Quantas linhas entram no primeiro render. A rede real tem 504 produtos e
 * jogar tudo de uma vez trava a rolagem em máquina de loja; o botão do rodapé
 * libera o resto para quem realmente quer varrer a lista inteira.
 */
const LINHAS_POR_VEZ = 100;

const Produtos: React.FC<Props> = ({ cardapio }) => {
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [limite, setLimite] = useState(LINHAS_POR_VEZ);

  const nomesDeCategoria = useMemo(() => {
    const vistos: Record<string, true> = {};
    const nomes: string[] = [];
    for (const item of cardapio.categorias) {
      if (vistos[item.nome]) continue;
      vistos[item.nome] = true;
      nomes.push(item.nome);
    }
    return nomes.sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [cardapio]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return cardapio.produtos.filter((produto) => {
      // Busca por NOME ou por CÓDIGO: no PDV o operador conhece o produto pelo
      // código, e é por ele que quem vai limpar o cadastro procura.
      const casaTermo =
        termo === "" ||
        produto.nome.toLowerCase().indexOf(termo) >= 0 ||
        produto.codigo.toLowerCase().indexOf(termo) >= 0;

      const casaCategoria =
        categoria === "todas" ||
        (categoria === "__sem__"
          ? produto.categorias.length === 0
          : produto.categorias.indexOf(categoria) >= 0);

      return casaTermo && casaCategoria;
    });
  }, [cardapio, busca, categoria]);

  const visiveis = filtrados.slice(0, limite);

  return (
    <>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              inventory_2
            </AlfredoStyles.MaterialIcons>
            Produtos
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            {cardapio.produtos.length} produtos cadastrados na rede. O preço
            mostrado é o da categoria em que o produto está.
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      <AlfredoStyles.BarraBusca>
        <AlfredoStyles.CampoBusca>
          <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
            search
          </AlfredoStyles.MaterialIcons>
          <AlfredoStyles.EntradaBusca
            value={busca}
            placeholder="Buscar por nome ou código"
            onChange={(e) => {
              setBusca(e.target.value);
              setLimite(LINHAS_POR_VEZ);
            }}
          />
        </AlfredoStyles.CampoBusca>

        <AlfredoStyles.Seletor
          value={categoria}
          onChange={(e) => {
            setCategoria(e.target.value);
            setLimite(LINHAS_POR_VEZ);
          }}
        >
          <option value="todas">Todas as categorias</option>
          <option value="__sem__">Sem categoria</option>
          {nomesDeCategoria.map((nome) => (
            <option key={nome} value={nome}>
              {nome}
            </option>
          ))}
        </AlfredoStyles.Seletor>
      </AlfredoStyles.BarraBusca>

      <AlfredoStyles.ContadorResultado>
        {filtrados.length === cardapio.produtos.length
          ? `${filtrados.length} produtos`
          : `${filtrados.length} de ${cardapio.produtos.length} produtos`}
        {visiveis.length < filtrados.length &&
          ` — mostrando os ${visiveis.length} primeiros`}
      </AlfredoStyles.ContadorResultado>

      {filtrados.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🔎</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhum produto encontrado
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Nenhum produto casa com o que foi digitado e com a categoria
            escolhida.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        <AlfredoStyles.TabelaEnvolucro>
          <AlfredoStyles.Tabela>
            <thead>
              <tr>
                <AlfredoStyles.Th>Código</AlfredoStyles.Th>
                <AlfredoStyles.Th>Produto</AlfredoStyles.Th>
                <AlfredoStyles.Th>Categoria</AlfredoStyles.Th>
                <AlfredoStyles.Th $direita>Preço</AlfredoStyles.Th>
                <AlfredoStyles.Th>Marcas</AlfredoStyles.Th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((produto) => (
                <AlfredoStyles.Tr key={produto.uuid || produto.codigo}>
                  <AlfredoStyles.Td $mono>
                    {produto.codigo || "—"}
                  </AlfredoStyles.Td>
                  <AlfredoStyles.Td>{produto.nome}</AlfredoStyles.Td>
                  <AlfredoStyles.Td>
                    {produto.categorias.length > 0 ? (
                      produto.categorias.join(", ")
                    ) : (
                      <AlfredoStyles.Etiqueta $tom="media">
                        sem categoria
                      </AlfredoStyles.Etiqueta>
                    )}
                  </AlfredoStyles.Td>
                  <AlfredoStyles.Td $direita $mono>
                    {formatarReais(produto.preco)}
                  </AlfredoStyles.Td>
                  <AlfredoStyles.Td>
                    {produto.combo && (
                      <AlfredoStyles.Etiqueta>combo</AlfredoStyles.Etiqueta>
                    )}{" "}
                    {produto.taxaServico && (
                      <AlfredoStyles.Etiqueta>
                        taxa de serviço
                      </AlfredoStyles.Etiqueta>
                    )}{" "}
                    {!produto.ativo && (
                      <AlfredoStyles.Etiqueta $tom="media">
                        inativo
                      </AlfredoStyles.Etiqueta>
                    )}
                    {produto.valorIlegivel && (
                      <AlfredoStyles.Etiqueta $tom="alta">
                        cadastro ilegível
                      </AlfredoStyles.Etiqueta>
                    )}
                  </AlfredoStyles.Td>
                </AlfredoStyles.Tr>
              ))}
            </tbody>
          </AlfredoStyles.Tabela>
        </AlfredoStyles.TabelaEnvolucro>
      )}

      {visiveis.length < filtrados.length && (
        <AlfredoStyles.BotaoTexto onClick={() => setLimite(filtrados.length)}>
          <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
            expand_more
          </AlfredoStyles.MaterialIcons>
          Mostrar todos os {filtrados.length}
        </AlfredoStyles.BotaoTexto>
      )}
    </>
  );
};

export default Produtos;
